import { Descriptions, Row , Card as AntdCard, message, } from "antd";
import React, { Fragment } from "react";
import {
  Opportunity,
  OpportunityProspectTypes,
  AssociatedEmail,
  AssociatedPhone,
  AssociatedAddress,
  CustomerCompanyAssociation,
  Beneficiary,
  PaymentAccount,
  FileUpload,
} from "../../../helpers/shared-interfaces";
import { capitalizeFirstLetter } from "../../../helpers/utilities";

import ContactDetailsView from "../../../components/ContactDetailsView";
import CustomerBeneficiariesView from "../../../components/CustomerBeneficiariesView";
import CustomerAssociationsView from "../../../components/CustomerAssociationsView";
import PaymentAccountsView from "../../../components/PaymentsAccountsView";
import { CheckCircleTwoTone } from "@ant-design/icons";
import TagsSection from "../../../components/TagsSection";
import EntitledSection from "../../../components/EntitledSection";
import entitlements from "../../../config/entitlements";
import { generateNameFromCustomer } from "../../../helpers/utilities";

interface Props {
  opportunity: Opportunity;
  editButton: JSX.Element;
  onAddEmail?: () => void;
  onAddPhone?: () => void;
  onAddAddress?: () => void;
  onMakePrimaryEmail?: (email: AssociatedEmail) => void;
  onMakePrimaryPhone?: (phone: AssociatedPhone) => void;
  onMakePrimaryAddress?: (address: AssociatedAddress) => void;
  onEditEmail?: (email: AssociatedEmail) => void;
  onDeleteEmail?: (email: AssociatedEmail) => void;
  onReactivateEmail?: (email: AssociatedEmail) => void;
  onEditPhone?: (phone: AssociatedPhone) => void;
  onDeletePhone?: (phone: AssociatedPhone) => void;
  onReactivatePhone?: (phone: AssociatedPhone) => void;
  onEditAddress?: (address: AssociatedAddress) => void;
  onDeleteAddress?: (address: AssociatedAddress) => void;
  onReactivateAddress?: (address: AssociatedAddress) => void;
  onAddBackgroundInfo?: () => void;
  onEditBackgroundInfo?: () => void;
  onDeleteBackgroundInfo?: () => void;
  onUploadCustomerInfoFile?: () => void;
  onDeleteCustomerInfoFile?: (id?: string) => void;
  onSetEntityToBeDeleted?: (entity: string) => void;
  onAddAssociation?: () => void;
  onEditAssociation?: (association: CustomerCompanyAssociation) => void;
  onDeleteAssociation?: (association: CustomerCompanyAssociation) => void;
  onMarkAssociationAsFormer?: (association: CustomerCompanyAssociation) => void;
  onMarkAssociationAsCurrent?: (
    association: CustomerCompanyAssociation,
  ) => void;
  onAddBeneficiary?: () => void;
  onEditBeneficiary?: (beneficiary: Beneficiary) => void;
  onDeleteBeneficiary?: (beneficiary: Beneficiary) => void;
  onAddPayoutAccount?: () => void;
  onAddDebitAccount?: () => void;
  onEditPayoutAccount?: (account: PaymentAccount) => void;
  onDeletePayoutAccount?: (account: PaymentAccount) => void;
  onEditDebitAccount?: (account: PaymentAccount) => void;
  onDeleteDebitAccount?: (account: PaymentAccount) => void;
  customerInfoFileUploads?: FileUpload[];
}

export default function (props: Props): JSX.Element {
  const { opportunity, editButton } = props;
  const copyToClipboard = (text: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        message.success("Copied to clipboard");
      })
      .catch(() => {
        message.error("Failed to copy text");
      });
  };

  const renderCopyableValue = (value: string) => {
    return (
      <div
        onClick={() => value && copyToClipboard(value)}
        style={{
          cursor: "pointer",
          padding: "6px 10px",
          margin: "-6px -10px",
        }}
      >
        {value || "n/a"}
      </div>
    );
  };
  return (
    <Fragment>
      {opportunity.opportunityProspect &&
        opportunity.opportunityProspect.name ===
          OpportunityProspectTypes.retailClient && (
          <div>
            {opportunity.customer && (
              <Row className="view-opportunity-row">
                <AntdCard
                  title={
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 12 }}
                    >
                      <span>Customer</span>
                      {opportunity.customer.tags.length > 0 && (
                        <TagsSection
                          compact
                          style={{ margin: 0 }}
                          tags={opportunity.customer.tags}
                        />
                      )}
                      <div
                        style={{ marginLeft: "auto", display: "flex", gap: 8 }}
                      >
                        <EntitledSection
                          entitlement={entitlements.Customers.edit}
                        >
                          {editButton}
                        </EntitledSection>
                      </div>
                    </div>
                  }
                >
                  <Descriptions>
                    <Descriptions.Item label={<b>Name</b>} key="Name">
                      {renderCopyableValue(generateNameFromCustomer(opportunity.customer, false))}
                    </Descriptions.Item>
                    <Descriptions.Item label={<b>Petra ID</b>} key="Petra ID">
                      {renderCopyableValue(opportunity.customer.petraId)}
                    </Descriptions.Item>

                    <Descriptions.Item
                      label={<b>SSNIT Number</b>}
                      key="ssnitNumber"
                    >
                      {renderCopyableValue(opportunity.customer.ssnitNumber ?? "N/A")}
                    </Descriptions.Item>

                    <Descriptions.Item
                      label={<b>Date of Birth</b>}
                      key="dateOfBirth"
                    >
                      {opportunity.customer.dateOfBirth ? (
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          {renderCopyableValue(
                            opportunity.customer.dateOfBirth,
                          )}
                          {opportunity.customer.isDobVerified && (
                            <CheckCircleTwoTone
                              twoToneColor="#52c41a"
                              title="Verified"
                            />
                          )}
                        </span>
                      ) : (
                        "N/A"
                      )}
                    </Descriptions.Item>

                    <Descriptions.Item
                      label={<b>Marital Status</b>}
                      key="Marital Status"
                    >
                      {opportunity.customer.maritalStatus
                        ? renderCopyableValue(
                            capitalizeFirstLetter(
                              opportunity.customer.maritalStatus.name,
                            ),
                          )
                        : "N/A"}
                    </Descriptions.Item>

                    <Descriptions.Item label={<b>Country</b>} key="Country">
                      {opportunity.customer.country
                        ? capitalizeFirstLetter(
                            opportunity.customer.country.name,
                          )
                        : "N/A"}
                    </Descriptions.Item>

                    <Descriptions.Item label={<b>Gender</b>} key="Gender">
                      {opportunity.customer.gender
                        ? renderCopyableValue(
                            capitalizeFirstLetter(opportunity.customer.gender),
                          )
                        : "N/A"}
                    </Descriptions.Item>

                    <Descriptions.Item label={<b>ID Type</b>} key="ID Type">
                      {opportunity.customer.idType
                        ? renderCopyableValue(opportunity.customer.idType.name)
                        : "N/A"}
                    </Descriptions.Item>

                    <Descriptions.Item label={<b>ID Number</b>} key="ID Number">
                      {opportunity.customer.photoIdNumber
                        ? renderCopyableValue(
                            capitalizeFirstLetter(
                              opportunity.customer.photoIdNumber,
                            ),
                          )
                        : "N/A"}
                    </Descriptions.Item>

                    <Descriptions.Item
                      label={<b>Ghana Card ID Number</b>}
                      key="Ghana Card ID Number"
                    >
                      {opportunity.customer.ghanaCardNo ? (
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          {renderCopyableValue(
                            capitalizeFirstLetter(
                              opportunity.customer.ghanaCardNo,
                            ),
                          )}
                          {opportunity.customer.isGhanaCardVerified && (
                            <CheckCircleTwoTone
                              twoToneColor="#52c41a"
                              title="Verified"
                            />
                          )}
                        </span>
                      ) : (
                        "N/A"
                      )}
                    </Descriptions.Item>

                    <Descriptions.Item
                      label={<b>Ghana Card ID Issue Date</b>}
                      key="idIssueDate"
                    >
                      {opportunity.customer.idIssueDate
                        ? renderCopyableValue(opportunity.customer.idIssueDate)
                        : "N/A"}
                    </Descriptions.Item>

                    <Descriptions.Item
                      label={<b>Ghana Card ID</b>}
                      key="Ghana Card ID"
                    >
                      {opportunity.customer.ghanaCardIdUrl ? (
                        <a
                          target="_blank"
                          href={opportunity.customer.ghanaCardIdUrl}
                          rel="noopener noreferrer"
                        >
                          Click To Open Image
                        </a>
                      ) : (
                        "N/A"
                      )}
                    </Descriptions.Item>

                    <Descriptions.Item
                      label={<b>Ghana Card ID (Back)</b>}
                      key="Ghana Card ID (Back))"
                    >
                      {opportunity.customer.ghanaCardIdBackUrl ? (
                        <a
                          target="_blank"
                          href={opportunity.customer.ghanaCardIdBackUrl}
                          rel="noopener noreferrer"
                        >
                          Click To Open Image
                        </a>
                      ) : (
                        "N/A"
                      )}
                    </Descriptions.Item>

                    <Descriptions.Item label={<b>ID</b>} key="ID">
                      {opportunity.customer.photoIdUrl ? (
                        <a
                          target="_blank"
                          href={opportunity.customer.photoIdUrl}
                          rel="noopener noreferrer"
                        >
                          Click To Open Image
                        </a>
                      ) : (
                        "N/A"
                      )}
                    </Descriptions.Item>

                    <Descriptions.Item
                      label={<b>Customer Signature</b>}
                      key="Customer Signature"
                    >
                      {opportunity.customer.signatureUrl ? (
                        <a
                          target="_blank"
                          href={opportunity.customer.signatureUrl}
                          rel="noopener noreferrer"
                        >
                          Click To Open Image
                        </a>
                      ) : (
                        "N/A"
                      )}
                    </Descriptions.Item>
                  </Descriptions>
                </AntdCard>
              </Row>
            )}

            {opportunity.customer &&
              opportunity.opportunityProspect &&
              opportunity.opportunityProspect.name ===
                OpportunityProspectTypes.retailClient && (
                <ContactDetailsView
                  phones={opportunity.customer.activeCustomerPhones || []}
                  emails={opportunity.customer.customerEmails || []}
                  adresses={opportunity.customer.customerAddresses || []}
                  formerEmployees={{ data: [], totalCount: 0 }}
                  currentEmployees={{ data: [], totalCount: 0 }}
                  style={{ marginBottom: "30px" }}
                  customer={opportunity.customer}
                  customerInfoFileUploads={
                    props.customerInfoFileUploads ||
                    opportunity.customer.customerInfoFileUploads ||
                    []
                  }
                  onUploadCustomerInfoFile={props.onUploadCustomerInfoFile}
                  onDeleteCustomerInfoFile={props.onDeleteCustomerInfoFile}
                  onSetEntityToBeDeleted={props.onSetEntityToBeDeleted}
                  onAddEmail={props.onAddEmail}
                  onAddPhone={props.onAddPhone}
                  onAddAddress={props.onAddAddress}
                  onMakePrimaryEmail={props.onMakePrimaryEmail}
                  onMakePrimaryPhone={props.onMakePrimaryPhone}
                  onMakePrimaryAddress={props.onMakePrimaryAddress}
                  onEditEmail={props.onEditEmail}
                  onDeleteEmail={props.onDeleteEmail}
                  onReactivateEmail={props.onReactivateEmail}
                  onEditPhone={props.onEditPhone}
                  onDeletePhone={props.onDeletePhone}
                  onReactivatePhone={props.onReactivatePhone}
                  onEditAddress={props.onEditAddress}
                  onDeleteAddress={props.onDeleteAddress}
                  onReactivateAddress={props.onReactivateAddress}
                  onAddBackgroundInfo={props.onAddBackgroundInfo}
                  onEditBackgroundInfo={props.onEditBackgroundInfo}
                  onDeleteBackgroundInfo={props.onDeleteBackgroundInfo}
                />
              )}
            <br />
            <PaymentAccountsView
              style={{ marginBottom: "30px" }}
              payoutAccounts={
                opportunity.customer?.customerPayoutAccounts || []
              }
              debitAccounts={opportunity.customer?.customerDebitAccounts || []}
              onAddPayoutAccount={props.onAddPayoutAccount}
              onAddDebitAccount={props.onAddDebitAccount}
              onEditPayoutAccount={props.onEditPayoutAccount}
              onDeletePayoutAccount={props.onDeletePayoutAccount}
              onEditDebitAccount={props.onEditDebitAccount}
              onDeleteDebitAccount={props.onDeleteDebitAccount}
            />

            <CustomerBeneficiariesView
              style={{ marginTop: "20px" }}
              subscriptions={opportunity.customerSubscriptions || []}
              onAddBeneficiary={props.onAddBeneficiary}
              onEditBeneficiary={props.onEditBeneficiary}
              onDeleteBeneficiary={props.onDeleteBeneficiary}
            />

            {opportunity.customer &&
              opportunity.customer?.customerCompanyAssociations?.length > 0 && (
                <CustomerAssociationsView
                  style={{ marginTop: "20px" }}
                  employers={opportunity.customer?.employers || []}
                  formerCompanies={opportunity.customer?.formerCompanies || []}
                  companyAssociations={
                    opportunity.customer?.customerCompanyAssociations || []
                  }
                  onAddAssociation={props.onAddAssociation}
                  onEditAssociation={props.onEditAssociation}
                  onDeleteAssociation={props.onDeleteAssociation}
                  onMarkAssociationAsFormer={props.onMarkAssociationAsFormer}
                  onMarkAssociationAsCurrent={props.onMarkAssociationAsCurrent}
                />
              )}
          </div>
        )}
    </Fragment>
  );
}
